export type BrowserElementFingerprint = {
    tag: string
    role: string
    type: string
    name: string
    actionable: boolean
    scrollable: boolean
}

export type BrowserTypeMode = "replace" | "append"

export function highImpactControlName(name: string): boolean {
    return /\b(buy|purchase|place order|confirm order|checkout|add to cart|pay|subscribe|unsubscribe|sign up|register|enroll|create|save|submit|confirm|approve|reject|delete|close account|remove account|transfer|send|email|message|contact|wire money|publish|post|comment|share|invite|follow|like|vote|rsvp|accept|agree|book|reserve|grant access|allow permission|authorize|install|uninstall|make public|make private)\b/i.test(
        name
    )
}

const browserDomHelpers = `
    const interactiveSelector = "a[href],button,input,textarea,select,[contenteditable='true'],[draggable='true'],[role='button'],[role='link'],[role='textbox'],[role='checkbox'],[role='radio'],[role='switch'],[role='tab'],[role='menuitem'],[role='option']";
    const scopeSelector = "main,article,section,nav,aside,form,table,[role='main'],[role='region'],[role='dialog'],[role='group'],[role='list'],[role='table'],[aria-label],[aria-labelledby]";
    const submitSelector = "button:not([type]),button[type=submit],input[type=submit],input[type=image]";
    const roots = [];
    let inspectedRootElements = 0;
    const visitRoot = (root) => {
        if (!root || roots.includes(root) || roots.length >= 64 || inspectedRootElements >= 20000) return;
        roots.push(root);
        for (const candidate of root.querySelectorAll("*")) {
            inspectedRootElements += 1;
            if (inspectedRootElements >= 20000 || roots.length >= 64) break;
            if (candidate.shadowRoot) visitRoot(candidate.shadowRoot);
            if (candidate.tagName === "IFRAME") {
                try {
                    if (candidate.contentDocument) visitRoot(candidate.contentDocument);
                } catch {}
            }
        }
    };
    visitRoot(document);
    const elementName = (el) => {
        const label = el.labels && el.labels[0] ? el.labels[0].innerText : "";
        const labelledBy = String(el.getAttribute("aria-labelledby") || "")
            .split(/\\s+/)
            .filter(Boolean)
            .map((id) => el.ownerDocument.getElementById(id)?.innerText || "")
            .join(" ");
        const nativeLabel = el.matches("input[type=submit],input[type=button],input[type=reset]")
            ? el.value
            : el.matches("input[type=image]")
              ? el.alt
              : "";
        return String(el.getAttribute("aria-label") || labelledBy || label || el.getAttribute("placeholder") || nativeLabel || el.getAttribute("title") || el.innerText || "")
            .replace(/\\s+/g, " ")
            .trim()
            .slice(0, 300);
    };
    const isSensitive = (el) => {
        const type = String(el.getAttribute("type") || "").toLowerCase();
        const autocomplete = String(el.getAttribute("autocomplete") || "").toLowerCase();
        const identity = String(el.getAttribute("name") || "") + " " + String(el.id || "") + " " + autocomplete + " " + elementName(el);
        return ["password", "file"].includes(type) || /(cc-|card|payment|one.?time|otp|passcode|password|cvv|cvc)/i.test(identity);
    };
    const isScrollable = (el) => {
        const view = el.ownerDocument.defaultView;
        const style = view ? view.getComputedStyle(el) : null;
        if (!style) return false;
        const vertical = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1;
        const horizontal = /(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth + 1;
        return vertical || horizontal;
    };
    const isActionable = (el) => el.matches(interactiveSelector);
    const submissionIntent = (el) => {
        const form = el.form;
        if (!form) return "other";
        const identity = [
            elementName(el),
            form.getAttribute("action") || "",
            form.getAttribute("aria-label") || "",
            form.getAttribute("name") || "",
            form.id || "",
            String(form.innerText || "").slice(0, 500)
        ].join(" ");
        if (String(el.getAttribute("type") || "").toLowerCase() === "search" || /\\b(search|find)\\b/i.test(identity)) return "search";
        if (/\\b(sign in|log in|login|oauth|sso|single sign-on|authenticate|authentication|continue with|connect with)\\b/i.test(identity)) return "authentication";
        return "other";
    };
    const fingerprintFor = (el) => ({
        tag: el.tagName.toLowerCase(),
        role: String(el.getAttribute("role") || ""),
        type: String(el.getAttribute("type") || "").toLowerCase(),
        name: elementName(el),
        actionable: isActionable(el),
        scrollable: isScrollable(el)
    });
    const topRect = (el) => {
        const rect = el.getBoundingClientRect();
        let x = rect.left;
        let y = rect.top;
        let view = el.ownerDocument.defaultView;
        while (view && view !== window) {
            const frame = view.frameElement;
            if (!frame) break;
            const frameRect = frame.getBoundingClientRect();
            x += frameRect.left + frame.clientLeft;
            y += frameRect.top + frame.clientTop;
            view = frame.ownerDocument.defaultView;
        }
        return { x, y, width: rect.width, height: rect.height };
    };
    const isVisible = (el) => {
        const rect = topRect(el);
        const view = el.ownerDocument.defaultView;
        const style = view ? view.getComputedStyle(el) : null;
        return rect.width > 0 && rect.height > 0 && rect.x < innerWidth && rect.y < innerHeight && rect.x + rect.width > 0 && rect.y + rect.height > 0 && style?.visibility !== "hidden" && style?.display !== "none";
    };
    const findRef = (ref) => {
        for (const root of roots) {
            const found = root.querySelector('[data-wildlark-browser-ref="' + CSS.escape(ref) + '"]');
            if (found) return found;
        }
        return null;
    };
    const rootWithin = (root, scope) => {
        let boundary = root?.host || root?.defaultView?.frameElement || null;
        for (let depth = 0; boundary && depth < 20; depth += 1) {
            if (boundary === scope || scope.contains(boundary)) return true;
            const parentRoot = boundary.getRootNode?.();
            boundary = parentRoot?.host || parentRoot?.defaultView?.frameElement || null;
        }
        return false;
    };
    const hitTest = (x, y) => {
        let localX = x;
        let localY = y;
        let element = document.elementFromPoint(localX, localY);
        for (let depth = 0; element && depth < 20; depth += 1) {
            if (element.shadowRoot) {
                const nested = element.shadowRoot.elementFromPoint(localX, localY);
                if (nested && nested !== element) {
                    element = nested;
                    continue;
                }
            }
            if (element.tagName === "IFRAME") {
                const frameRect = element.getBoundingClientRect();
                try {
                    const frameDocument = element.contentDocument;
                    if (!frameDocument) return { element, opaque: true };
                    localX -= frameRect.left + element.clientLeft;
                    localY -= frameRect.top + element.clientTop;
                    element = frameDocument.elementFromPoint(localX, localY);
                    continue;
                } catch {
                    return { element, opaque: true };
                }
            }
            break;
        }
        if (!element) return { element: null, opaque: false };
        const semantic = element.closest(interactiveSelector + ",canvas,[role='application']") || element;
        const opaque = semantic.matches("canvas,[role='application']") || (semantic.tagName === "IFRAME" && !semantic.contentDocument);
        return { element: semantic, opaque };
    };
`

export function readPageScript(options: { generation: number; filter: "interactive" | "all"; maxChars: number; scopeRef?: string }): string {
    return `(() => {
        ${browserDomHelpers}
        const requestedScope = ${options.scopeRef ? `findRef(${JSON.stringify(options.scopeRef)})` : "document.body"};
        if (!requestedScope || !requestedScope.isConnected) return { ok: false, code: "stale_ref" };
        for (const root of roots) root.querySelectorAll("[data-wildlark-browser-ref]").forEach((el) => el.removeAttribute("data-wildlark-browser-ref"));
        const candidates = new Set();
        const interactiveOnly = ${JSON.stringify(options.filter)} === "interactive";
        const candidateRoots = requestedScope === document.body
            ? roots
            : [requestedScope, ...roots.filter((root) => root !== requestedScope && rootWithin(root, requestedScope))];
        const addCandidate = (candidate) => {
            if (candidates.size < 1200) candidates.add(candidate);
        };
        for (const root of candidateRoots) {
            if (candidates.size >= 1200) break;
            if (root.matches?.(interactiveSelector) || (root.nodeType === 1 && isScrollable(root))) addCandidate(root);
            for (const el of root.querySelectorAll(interactiveSelector)) {
                addCandidate(el);
                if (candidates.size >= 1200) break;
            }
            for (const el of root.querySelectorAll("[aria-label],[aria-labelledby],[role],main,section,aside,nav,div,ul,ol")) {
                if (isScrollable(el) || (!interactiveOnly && el.matches(scopeSelector))) addCandidate(el);
                if (candidates.size >= 1200) break;
            }
        }
        const elements = [];
        const fingerprints = {};
        let index = 0;
        for (const el of candidates) {
            if (index >= 240 || !isVisible(el)) continue;
            if (isSensitive(el)) {
                elements.push({ sensitive: true, marker: "[sensitive field — user input only]" });
                continue;
            }
            const fingerprint = fingerprintFor(el);
            const rect = topRect(el);
            const ref = ${JSON.stringify(String(options.generation))} + ":" + index++;
            el.setAttribute("data-wildlark-browser-ref", ref);
            const options = el.matches("select")
                ? Array.from(el.options).slice(0, 50).map((option) => ({
                    value: String(option.value).slice(0, 1000),
                    label: String(option.textContent || "").replace(/\\s+/g, " ").trim().slice(0, 300),
                    selected: Boolean(option.selected),
                    disabled: Boolean(option.disabled)
                }))
                : undefined;
            elements.push({
                ref,
                ...fingerprint,
                disabled: Boolean(el.disabled),
                readonly: Boolean(el.readOnly),
                required: Boolean(el.required),
                ...(typeof el.checked === "boolean" ? { checked: el.checked } : {}),
                ...(el.hasAttribute("aria-expanded") ? { expanded: el.getAttribute("aria-expanded") === "true" } : {}),
                ...(el.hasAttribute("aria-pressed") ? { pressed: el.getAttribute("aria-pressed") === "true" } : {}),
                ...(el.hasAttribute("aria-selected") ? { selected: el.getAttribute("aria-selected") === "true" } : {}),
                ...(options ? { options } : {}),
                bounds: { x: Math.round(rect.x), y: Math.round(rect.y), width: Math.round(rect.width), height: Math.round(rect.height) }
            });
            fingerprints[ref] = fingerprint;
        }
        const textParts = [];
        const headingElements = [];
        if (!interactiveOnly) {
            for (const root of candidateRoots) {
                const textRoot = root.nodeType === 9 ? root.body : root;
                const part = String(textRoot?.innerText || textRoot?.textContent || "").trim();
                if (part && !textParts.includes(part)) textParts.push(part);
                if (root.matches?.("h1,h2,h3,h4,h5,h6,[role=heading]")) headingElements.push(root);
                for (const heading of root.querySelectorAll("h1,h2,h3,h4,h5,h6,[role=heading]")) {
                    if (!headingElements.includes(heading)) headingElements.push(heading);
                    if (headingElements.length >= 100) break;
                }
                if (headingElements.length >= 100) break;
            }
        }
        const text = textParts.join("\\n\\n").slice(0, ${options.maxChars});
        const headings = headingElements
            .filter(isVisible)
            .slice(0, 100)
            .map((el) => ({ level: Number(el.getAttribute("aria-level")) || Number(el.tagName.slice(1)) || 0, text: elementName(el) }));
        return {
            ok: true,
            title: String(document.title || "").slice(0, 500),
            url: location.href,
            text,
            headings,
            viewport: { width: innerWidth, height: innerHeight, scrollX, scrollY, devicePixelRatio },
            elements,
            fingerprints
        };
    })()`
}

export function extractContentScript(maxChars: number): string {
    return `(() => {
        const candidates = ["article", "main", "[role=main]", "#content", ".content", ".article", ".post"];
        let root = null;
        for (const selector of candidates) {
            const matches = Array.from(document.querySelectorAll(selector));
            const best = matches.sort((a, b) => String(b.innerText || "").length - String(a.innerText || "").length)[0];
            if (best && String(best.innerText || "").trim().length > 200) { root = best; break; }
        }
        if (!root) root = document.body;
        const clone = root.cloneNode(true);
        clone.querySelectorAll("script,style,noscript,nav,header,footer,aside,form,button,svg,canvas,[hidden],[aria-hidden=true]").forEach((el) => el.remove());
        const text = String(clone.innerText || clone.textContent || "").replace(/\\n{3,}/g, "\\n\\n").trim().slice(0, ${maxChars});
        const metadata = (name, property) => document.querySelector('meta[name="' + name + '"],meta[property="' + property + '"]')?.getAttribute("content") || "";
        return {
            title: metadata("", "og:title") || document.title || "",
            url: location.href,
            byline: metadata("author", "article:author"),
            description: metadata("description", "og:description"),
            text
        };
    })()`
}

export function findInPageScript(query: string, maxResults: number): string {
    return `(() => {
        ${browserDomHelpers}
        const needle = ${JSON.stringify(query.toLowerCase())};
        const results = [];
        const seen = new Set();
        for (const root of roots) {
            for (const el of root.querySelectorAll("[data-wildlark-browser-ref],h1,h2,h3,h4,h5,h6,p,li,td,th")) {
                if (results.length >= ${maxResults}) break;
                if (!isVisible(el)) continue;
                const text = String(el.innerText || elementName(el) || "").replace(/\\s+/g, " ").trim();
                if (!text || !text.toLowerCase().includes(needle)) continue;
                const ref = el.getAttribute("data-wildlark-browser-ref") || undefined;
                const key = (ref || "") + "\\u0000" + text.slice(0, 240);
                if (seen.has(key)) continue;
                seen.add(key);
                results.push({ ...(ref ? { ref } : {}), text: text.slice(0, 500) });
            }
        }
        return { title: document.title || "", url: location.href, query: ${JSON.stringify(query)}, results };
    })()`
}

export function screenshotMetricsScript(): string {
    return `(() => ({ url: location.href, viewport: { width: innerWidth, height: innerHeight, scrollX, scrollY, devicePixelRatio } }))()`
}

export function prepareClickScript(ref: string, fingerprint: BrowserElementFingerprint): string {
    return `(() => {
        ${browserDomHelpers}
        const el = findRef(${JSON.stringify(ref)});
        if (!el || !el.isConnected) return { ok: false, code: "stale_ref" };
        if (isSensitive(el)) return { ok: false, code: "sensitive_control" };
        if (!isActionable(el)) return { ok: false, code: "not_actionable" };
        if (JSON.stringify(fingerprintFor(el)) !== JSON.stringify(${JSON.stringify(fingerprint)})) return { ok: false, code: "stale_ref" };
        if (el.disabled) return { ok: false, code: "disabled_control" };
        el.scrollIntoView({ block: "center", inline: "center" });
        const rect = topRect(el);
        const x = Math.round(rect.x + rect.width / 2);
        const y = Math.round(rect.y + rect.height / 2);
        const hit = hitTest(x, y);
        if (hit.element !== el && !el.contains(hit.element)) return { ok: false, code: "obscured_control" };
        return { ok: true, x, y, submit: Boolean(el.form) && el.matches(submitSelector), submissionIntent: submissionIntent(el), url: location.href, fingerprint: fingerprintFor(el) };
    })()`
}

export function prepareCoordinateClickScript(x: number, y: number): string {
    return `(() => {
        ${browserDomHelpers}
        if (!Number.isFinite(${x}) || !Number.isFinite(${y}) || ${x} < 0 || ${y} < 0 || ${x} >= innerWidth || ${y} >= innerHeight) return { ok: false, code: "outside_viewport" };
        const hit = hitTest(${x}, ${y});
        if (!hit.element) return { ok: false, code: "no_target" };
        if (hit.opaque) return { ok: true, x: ${x}, y: ${y}, opaque: true, url: location.href };
        const el = hit.element;
        if (!isActionable(el)) return { ok: false, code: "not_actionable" };
        if (isSensitive(el)) return { ok: false, code: "sensitive_control" };
        if (el.disabled) return { ok: false, code: "disabled_control" };
        return { ok: true, x: ${x}, y: ${y}, opaque: false, submit: Boolean(el.form) && el.matches(submitSelector), submissionIntent: submissionIntent(el), url: location.href, fingerprint: fingerprintFor(el) };
    })()`
}

export function prepareHoverScript(ref: string, fingerprint: BrowserElementFingerprint): string {
    return prepareClickScript(ref, fingerprint)
}

export function inspectTypeSubmissionScript(ref: string, fingerprint: BrowserElementFingerprint): string {
    return `(() => {
        ${browserDomHelpers}
        const el = findRef(${JSON.stringify(ref)});
        if (!el || !el.isConnected) return { ok: false, code: "stale_ref" };
        if (isSensitive(el)) return { ok: false, code: "sensitive_control" };
        if (JSON.stringify(fingerprintFor(el)) !== JSON.stringify(${JSON.stringify(fingerprint)})) return { ok: false, code: "stale_ref" };
        if (!(el.matches("input:not([type]),input[type=text],input[type=search],input[type=email],input[type=url],input[type=tel],textarea") || el.isContentEditable)) return { ok: false, code: "not_editable" };
        return { ok: true, submissionIntent: submissionIntent(el), url: location.href };
    })()`
}

export function prepareTypeScript(
    ref: string,
    fingerprint: BrowserElementFingerprint,
    mode: BrowserTypeMode,
    expectedSubmissionIntent?: "search" | "authentication" | "other"
): string {
    return `(() => {
        ${browserDomHelpers}
        const el = findRef(${JSON.stringify(ref)});
        if (!el || !el.isConnected) return { ok: false, code: "stale_ref" };
        if (isSensitive(el)) return { ok: false, code: "sensitive_control" };
        if (JSON.stringify(fingerprintFor(el)) !== JSON.stringify(${JSON.stringify(fingerprint)})) return { ok: false, code: "stale_ref" };
        if (!(el.matches("input:not([type]),input[type=text],input[type=search],input[type=email],input[type=url],input[type=tel],textarea") || el.isContentEditable)) return { ok: false, code: "not_editable" };
        const intent = submissionIntent(el);
        const expectedIntent = ${JSON.stringify(expectedSubmissionIntent ?? null)};
        if (expectedIntent && intent !== expectedIntent) return { ok: false, code: "stale_ref" };
        el.focus();
        if (${JSON.stringify(mode)} === "replace") {
            if (typeof el.select === "function") el.select();
            else {
                const selection = el.ownerDocument.getSelection();
                const range = el.ownerDocument.createRange();
                range.selectNodeContents(el);
                if (!selection) return { ok: false, code: "selection_unavailable" };
                selection.removeAllRanges();
                selection.addRange(range);
            }
        } else if (typeof el.setSelectionRange === "function") {
            const end = String(el.value || "").length;
            el.setSelectionRange(end, end);
        } else {
            const selection = el.ownerDocument.getSelection();
            const range = el.ownerDocument.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            if (!selection) return { ok: false, code: "selection_unavailable" };
            selection.removeAllRanges();
            selection.addRange(range);
        }
        return { ok: true, submissionIntent: intent, url: location.href };
    })()`
}

export function selectOptionScript(ref: string, fingerprint: BrowserElementFingerprint, value: string, expected?: { value: string; label: string }): string {
    return `(() => {
        ${browserDomHelpers}
        const el = findRef(${JSON.stringify(ref)});
        if (!el || !el.isConnected) return { ok: false, code: "stale_ref" };
        if (isSensitive(el)) return { ok: false, code: "sensitive_control" };
        if (JSON.stringify(fingerprintFor(el)) !== JSON.stringify(${JSON.stringify(fingerprint)})) return { ok: false, code: "stale_ref" };
        if (!el.matches("select") || el.disabled) return { ok: false, code: "not_selectable" };
        const requested = ${JSON.stringify(value)};
        const option = Array.from(el.options).find((candidate) => candidate.value === requested || String(candidate.textContent || "").trim() === requested);
        if (!option) return { ok: false, code: "option_not_found" };
        const prepared = { value: option.value, label: String(option.textContent || "").trim() };
        const expected = ${JSON.stringify(expected ?? null)};
        if (!expected) return { ok: true, ...prepared, url: location.href };
        if (prepared.value !== expected.value || prepared.label !== expected.label) return { ok: false, code: "stale_ref" };
        el.value = option.value;
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
        return { ok: true, ...prepared, url: location.href };
    })()`
}

export function prepareKeypressScript(key: string): string {
    return `(() => {
        ${browserDomHelpers}
        let el = document.activeElement;
        let focusInspectable = true;
        for (let depth = 0; el && depth < 20; depth += 1) {
            if (el.shadowRoot?.activeElement) { el = el.shadowRoot.activeElement; continue; }
            if (el.tagName === "IFRAME") {
                try {
                    if (el.contentDocument?.activeElement) { el = el.contentDocument.activeElement; continue; }
                    focusInspectable = false;
                } catch { focusInspectable = false; }
            }
            break;
        }
        if (${JSON.stringify(key)} === "Enter" && !focusInspectable) return { ok: true, submit: true, submissionIntent: "other", url: location.href };
        if (!el) return { ok: true, submit: false, submissionIntent: "other", url: location.href };
        const multiline = el.matches("textarea") || el.isContentEditable;
        const inputType = String(el.getAttribute("type") || "text").toLowerCase();
        const canSubmit = Boolean(el.form) && !multiline && !["button", "reset", "file"].includes(inputType);
        return { ok: true, submit: ${JSON.stringify(key)} === "Enter" && canSubmit, submissionIntent: submissionIntent(el), url: location.href };
    })()`
}

export function prepareDragScript(
    startRef: string,
    startFingerprint: BrowserElementFingerprint,
    endRef: string,
    endFingerprint: BrowserElementFingerprint
): string {
    return `(() => {
        ${browserDomHelpers}
        const start = findRef(${JSON.stringify(startRef)});
        const end = findRef(${JSON.stringify(endRef)});
        if (!start || !start.isConnected || !end || !end.isConnected) return { ok: false, code: "stale_ref" };
        if (isSensitive(start) || isSensitive(end)) return { ok: false, code: "sensitive_control" };
        if (JSON.stringify(fingerprintFor(start)) !== JSON.stringify(${JSON.stringify(startFingerprint)})) return { ok: false, code: "stale_ref" };
        if (JSON.stringify(fingerprintFor(end)) !== JSON.stringify(${JSON.stringify(endFingerprint)})) return { ok: false, code: "stale_ref" };
        if (!isVisible(start) || !isVisible(end)) return { ok: false, code: "control_not_visible" };
        const startRect = topRect(start);
        const endRect = topRect(end);
        const startX = Math.round(startRect.x + startRect.width / 2);
        const startY = Math.round(startRect.y + startRect.height / 2);
        const endX = Math.round(endRect.x + endRect.width / 2);
        const endY = Math.round(endRect.y + endRect.height / 2);
        const startHit = hitTest(startX, startY);
        const endHit = hitTest(endX, endY);
        if (startHit.opaque || endHit.opaque) return { ok: false, code: "opaque_control" };
        if (startHit.element !== start && !start.contains(startHit.element)) return { ok: false, code: "obscured_control" };
        if (endHit.element !== end && !end.contains(endHit.element)) return { ok: false, code: "obscured_control" };
        return {
            ok: true,
            startX,
            startY,
            endX,
            endY,
            url: location.href
        };
    })()`
}

export function scrollPageScript(direction: "up" | "down", amount: "half-page" | "page", ref?: string, fingerprint?: BrowserElementFingerprint): string {
    const factor = amount === "half-page" ? 0.5 : 0.9
    const sign = direction === "up" ? -1 : 1
    if (!ref || !fingerprint) {
        return `(() => { window.scrollBy({ top: innerHeight * ${factor * sign}, behavior: "instant" }); return { ok: true, scrollX, scrollY, url: location.href }; })()`
    }
    return `(() => {
        ${browserDomHelpers}
        const el = findRef(${JSON.stringify(ref)});
        if (!el || !el.isConnected) return { ok: false, code: "stale_ref" };
        if (JSON.stringify(fingerprintFor(el)) !== JSON.stringify(${JSON.stringify(fingerprint)})) return { ok: false, code: "stale_ref" };
        if (!isScrollable(el)) return { ok: false, code: "not_scrollable" };
        el.scrollBy({ top: el.clientHeight * ${factor * sign}, behavior: "instant" });
        return { ok: true, ref: ${JSON.stringify(ref)}, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop, url: location.href };
    })()`
}
